import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-primary text-white py-16">
        <div className="container mx-auto px-4">
          <Link 
            to="/" 
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-4xl font-bold">Política de Privacidad</h1>
              <p className="text-white/70 mt-1">Última actualización: Enero 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <motion.div 
          className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="prose prose-slate max-w-none">
            
            {/* Section 1 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                1. Responsable del tratamiento
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-2">
                <p><strong>Responsable:</strong> Stampply</p>
                <p><strong>Denominación social:</strong> XP Projects SaaS</p>
                <p><strong>NIF/CIF:</strong> 46636052X</p>
                <p><strong>Correo electrónico:</strong> <a href="mailto:info@stampply.com" className="text-primary hover:underline">info@stampply.com</a></p>
              </div>
              <p className="mt-4 text-slate-600">En adelante, "Stampply".</p>
            </section>

            {/* Section 2 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                2. Ámbito de aplicación
              </h2>
              <p className="text-slate-700 mb-4">La presente Política de Privacidad regula el tratamiento de los datos personales de:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
                <li>Usuarios comerciales (comercios que utilizan Stampply).</li>
                <li>Clientes finales de dichos comercios que participan en programas de fidelización mediante Stampply (sellos digitales por WhatsApp).</li>
                <li>Usuarios que navegan por la web de Stampply.</li>
              </ul>
              <p className="mt-4 text-slate-600">El tratamiento se realiza conforme al Reglamento (UE) 2016/679 (RGPD) y a la Ley Orgánica 3/2018 (LOPDGDD).</p>
            </section>

            {/* Section 3 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                3. Tipos de datos que tratamos
              </h2>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">3.1. Datos de comercios (usuarios profesionales)</h3>
              <ul className="list-disc list-inside space-y-1 text-slate-600 ml-4">
                <li>Nombre y apellidos del responsable</li>
                <li>Email profesional</li>
                <li>Teléfono</li>
                <li>Nombre del comercio</li>
                <li>Dirección del comercio</li>
                <li>Datos de acceso a la plataforma</li>
                <li>Datos de facturación (cuando proceda)</li>
              </ul>

              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">3.2. Datos de clientes finales (usuarios de sellos)</h3>
              <p className="text-slate-600 mb-3">Stampply no solicita datos innecesarios al cliente final. Los datos tratados pueden incluir:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600 ml-4">
                <li>Número de teléfono (asociado a WhatsApp)</li>
                <li>Identificador técnico del usuario en el sistema</li>
                <li>Historial de sellos acumulados y recompensas</li>
                <li>Fecha y hora de interacción</li>
                <li>Comercio asociado</li>
              </ul>
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mt-4 rounded-r-lg">
                <p className="text-amber-800 font-medium">⚠️ Stampply no solicita nombre, DNI, dirección postal ni datos sensibles a los clientes finales.</p>
              </div>

              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">3.3. Datos técnicos</h3>
              <ul className="list-disc list-inside space-y-1 text-slate-600 ml-4">
                <li>Dirección IP</li>
                <li>Datos de navegación</li>
                <li>Cookies (ver Política de Cookies)</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                4. Finalidades del tratamiento
              </h2>
              <p className="text-slate-700 mb-4">Tratamos los datos personales con las siguientes finalidades:</p>
              
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">a) Prestación del servicio</h4>
                  <ul className="list-disc list-inside space-y-1 text-slate-600 ml-4">
                    <li>Permitir la acumulación y validación de sellos digitales.</li>
                    <li>Gestionar recompensas y canjes.</li>
                    <li>Facilitar la comunicación vía WhatsApp relacionada con el programa de fidelización.</li>
                  </ul>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">b) Gestión de usuarios profesionales</h4>
                  <ul className="list-disc list-inside space-y-1 text-slate-600 ml-4">
                    <li>Alta y administración de cuentas de comercio.</li>
                    <li>Facturación y soporte.</li>
                    <li>Comunicaciones operativas sobre el servicio.</li>
                  </ul>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">c) Mejora del servicio</h4>
                  <ul className="list-disc list-inside space-y-1 text-slate-600 ml-4">
                    <li>Análisis agregado y anonimizado del uso de la plataforma.</li>
                    <li>Detección de errores y mejora de funcionalidades.</li>
                  </ul>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">d) Cumplimiento legal</h4>
                  <ul className="list-disc list-inside space-y-1 text-slate-600 ml-4">
                    <li>Cumplimiento de obligaciones legales y fiscales.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                5. Base legal del tratamiento
              </h2>
              <p className="text-slate-700 mb-4">Las bases jurídicas que legitiman el tratamiento son:</p>
              <div className="space-y-3">
                <div className="border-l-4 border-primary pl-4">
                  <p className="font-semibold text-slate-800">Ejecución de un contrato (art. 6.1.b RGPD)</p>
                  <p className="text-slate-600">Para usuarios comerciales y para la correcta prestación del servicio de fidelización.</p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <p className="font-semibold text-slate-800">Interés legítimo (art. 6.1.f RGPD)</p>
                  <p className="text-slate-600">Para la prevención del fraude, seguridad de la plataforma y mejora del servicio.</p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <p className="font-semibold text-slate-800">Consentimiento del interesado (art. 6.1.a RGPD)</p>
                  <p className="text-slate-600">En el caso de comunicaciones no estrictamente necesarias o cuando así lo requiera la normativa.</p>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                6. Uso de WhatsApp
              </h2>
              <p className="text-slate-700 mb-4">Stampply utiliza WhatsApp como canal de interacción entre el cliente final y el sistema de fidelización del comercio.</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
                <li>El cliente inicia voluntariamente la conversación escaneando un QR.</li>
                <li>Los mensajes enviados tienen finalidad estrictamente operativa (validación de sellos, estado del programa, recompensas).</li>
                <li>Stampply no envía spam ni comunicaciones promocionales propias al cliente final.</li>
                <li>WhatsApp es un servicio de terceros gestionado por WhatsApp LLC / Meta Platforms, que dispone de su propia política de privacidad.</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                7. Conservación de los datos
              </h2>
              <p className="text-slate-700 mb-4">Los datos se conservarán:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
                <li>Mientras exista una relación activa con el comercio.</li>
                <li>Mientras el cliente final participe en el programa de fidelización.</li>
                <li>Durante los plazos legalmente exigidos para obligaciones fiscales o legales.</li>
              </ul>
              <p className="mt-4 text-slate-600">Una vez finalizada la relación, los datos se eliminarán o anonimizarán de forma segura.</p>
            </section>

            {/* Section 8 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                8. Destinatarios y encargados del tratamiento
              </h2>
              <p className="text-slate-700 mb-4">Los datos podrán ser tratados por proveedores que actúan como encargados del tratamiento, tales como:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
                <li>Proveedores de hosting y cloud</li>
                <li>Proveedores de servicios de mensajería (WhatsApp)</li>
                <li>Plataformas de pago (cuando proceda)</li>
              </ul>
              <p className="mt-4 text-slate-600">Todos ellos cuentan con contratos de tratamiento de datos conforme al RGPD.</p>
              <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-4 rounded-r-lg">
                <p className="text-green-800 font-medium">⚠️ Stampply no vende ni cede datos personales a terceros.</p>
              </div>
            </section>

            {/* Section 9 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                9. Transferencias internacionales
              </h2>
              <p className="text-slate-700">Algunos proveedores tecnológicos pueden estar ubicados fuera del Espacio Económico Europeo. En estos casos, se adoptan las garantías adecuadas, como cláusulas contractuales tipo aprobadas por la Comisión Europea.</p>
            </section>

            {/* Section 10 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                10. Derechos de los interesados
              </h2>
              <p className="text-slate-700 mb-4">Cualquier persona puede ejercer los siguientes derechos:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {['Acceso', 'Rectificación', 'Supresión', 'Oposición', 'Limitación del tratamiento', 'Portabilidad de los datos'].map((derecho) => (
                  <div key={derecho} className="bg-primary/10 text-primary font-medium px-4 py-2 rounded-lg text-center text-sm">
                    {derecho}
                  </div>
                ))}
              </div>
              <p className="text-slate-600">Para ello, puede escribir a: <a href="mailto:privacy@stampply.com" className="text-primary font-medium hover:underline">privacy@stampply.com</a>, indicando el derecho que desea ejercer.</p>
              <p className="mt-4 text-slate-600">También tiene derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD).</p>
            </section>

            {/* Section 11 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                11. Seguridad de los datos
              </h2>
              <p className="text-slate-700">Stampply aplica medidas técnicas y organizativas adecuadas para garantizar la seguridad, confidencialidad e integridad de los datos personales y evitar accesos no autorizados, pérdidas o alteraciones.</p>
            </section>

            {/* Section 12 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                12. Cambios en la política de privacidad
              </h2>
              <p className="text-slate-700">Stampply se reserva el derecho a modificar la presente Política de Privacidad para adaptarla a novedades legales o técnicas. Las modificaciones serán publicadas en esta misma página.</p>
            </section>

            {/* Section 13 */}
            <section className="mb-6">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                13. Contacto
              </h2>
              <p className="text-slate-700">Para cualquier duda relacionada con esta Política de Privacidad: <a href="mailto:info@stampply.com" className="text-primary font-medium hover:underline">info@stampply.com</a></p>
            </section>

          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage;
